import React from 'react'
import { TextInput as PlatformInput, TextInputProps as NativeTextInputProps } from 'react-native'
import styled from 'styled-components/native'
import { useTheme } from 'styled-components/native'
import { isWeb } from 'APP/Helpers/checkPlatform'

import { baseColors } from 'APP/Theme/Theme'

import Infotip from 'APP/Converse/Inputs/Infotip'
import TooltipButton from 'APP/Components/TooltipButton'
import { IconVariantProps } from 'APP/Converse/Icon/types'
import Typography from 'APP/Converse/Typography'
import { ShadowView } from 'APP/Converse/ShadowView'
import Icon from 'APP/Converse/Icon'
import Styles from 'APP/Converse/Inputs/style'
import {
  StyledInputWrapper,
  StyledIconWrapper,
  StyledPlatformInput,
} from 'APP/Converse/Inputs/styles'

// Workaround until bottom margin can be removed from StyleInputContainer
// in APP/Converse/Inputs/styles
const StyledInputContainerWithOptionalMargin = styled.View<{ noBottomMargin?: boolean }>`
  gap: 4px;
  margin-bottom: ${({ theme, noBottomMargin }) =>
    noBottomMargin ? '0px' : `${theme.metrics.baseMargin}px`};
`

interface TooltipConfig {
  text?: string
  subtitle?: string
  img?: string
  iconColor?: string
}

interface TextInputProps extends Omit<NativeTextInputProps, 'placeholderTextColor'> {
  label: string
  infotip?: string
  isFocused?: boolean
  error?: string
  showRequiredAsterisk?: boolean
  isSecureEntry?: boolean
  disabled?: boolean
  placeholder?: string
  hide?: boolean
  tooltipConfig?: TooltipConfig
  id?: string
  icon?: string
  iconPosition?: 'left' | 'right'
  iconVariant?: IconVariantProps
  iconColor?: keyof typeof baseColors
  iconSize?: number
  noBottomMargin?: boolean
}

const TextInput = React.forwardRef<PlatformInput, TextInputProps>(
  (
    {
      label,
      infotip,
      isFocused,
      error,
      showRequiredAsterisk = false,
      isSecureEntry = false,
      disabled = false,
      placeholder,
      hide = false,
      tooltipConfig,
      id,
      icon,
      noBottomMargin,
      iconPosition = 'left',
      iconVariant,
      iconColor = 'switchTrackColor',
      iconSize = 20,
      ...inputProps
    }: TextInputProps,
    ref
  ) => {
    const theme = useTheme()
    const placeholderColor = error ? theme.colors.errorText : theme.colors.textDisabled

    const webEvents = isWeb() ? { onClick: inputProps.onPressIn } : {}
    const requiredAsterisk = showRequiredAsterisk && <Typography color="error"> *</Typography>
    const infotipId = id ? `${id}-infotip` : undefined
    const labelId = id ? id : label?.replaceAll(' ', '')

    if (hide) {
      return null
    }

    return (
      <StyledInputContainerWithOptionalMargin noBottomMargin={noBottomMargin}>
        {label && (
          <Typography variant="bodyNormalBold" color={error ? 'error' : 'text'} id={labelId}>
            {label}
            {requiredAsterisk}
          </Typography>
        )}
        <ShadowView>
          <StyledInputWrapper>
            {icon && iconVariant && (
              <StyledIconWrapper position={iconPosition}>
                <Icon
                  src={icon}
                  variant={iconVariant}
                  color={iconColor || 'text'}
                  size={iconSize}
                  testID={`text-input-icon-${icon}`}
                />
              </StyledIconWrapper>
            )}
            <StyledPlatformInput
              ref={ref}
              hasIcon={!!(icon && iconVariant)}
              iconPosition={iconPosition}
              iconSize={iconSize}
              secureTextEntry={isSecureEntry}
              disabled={disabled}
              editable={!disabled}
              placeholder={placeholder}
              placeholderTextColor={placeholderColor}
              aria-describedby={infotipId}
              aria-labelledby={labelId}
              {...inputProps}
              {...webEvents}
            />
          </StyledInputWrapper>
          {!!tooltipConfig && <TooltipButton config={tooltipConfig} style={Styles.tooltipStyles} />}
        </ShadowView>
        <Infotip error={error} infotip={infotip} isFocused={isFocused} id={infotipId} />
      </StyledInputContainerWithOptionalMargin>
    )
  }
)

TextInput.displayName = 'TextInput'

export default TextInput
